import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { sanitizeRichNotes } from "@/lib/sanitizeHtml";
import { uploadPhoto } from "@/lib/uploadPhoto";

/**
 * Notion-style inline editor used in CRM card notes.
 * - Plain text typing.
 * - Paste (Ctrl+V) of clipboard images → inlined as <img> at the caret.
 * - Click any inline image → opens lightbox (caller decides how to display).
 *
 * Stores HTML in the parent's `notes` state via the `onChange` callback.
 */
export function RichNotes({ initial, onChange, onImageClick, placeholder, testId, readOnly = false }) {
  const ref = useRef(null);
  const initedRef = useRef(false);

  // Initialize content exactly once when the editor mounts.
  useEffect(() => {
    if (initedRef.current || !ref.current) return;
    initedRef.current = true;
    const raw = String(initial || "");
    if (/<(img|br|div|p|span)\b/i.test(raw)) {
      ref.current.innerHTML = sanitizeRichNotes(raw);
    } else {
      const escaped = raw
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      ref.current.innerHTML = escaped;
    }
    onChange(ref.current.innerHTML);
  }, []);

  const emit = () => { if (ref.current) onChange(sanitizeRichNotes(ref.current.innerHTML)); };

  const insertImageAtCaret = (dataUrl) => {
    const img = document.createElement("img");
    img.src = dataUrl;
    img.setAttribute("alt", "screenshot");
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);
      range.setStartAfter(img);
      range.setEndAfter(img);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      ref.current?.appendChild(img);
    }
    emit();
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
    const blobs = items
      .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (blobs.length > 0) {
      e.preventDefault();
      for (const blob of blobs) {
        if (blob.size > 1.5 * 1024 * 1024) {
          toast.error(`Imagen demasiado grande (máx 1.5 MB)`);
          continue;
        }
        try {
          const result = await uploadPhoto(blob, { folder: "alendrapc/crm-notes", maxSide: 1600, quality: 0.85 });
          insertImageAtCaret(result);
        } catch (err) {
          toast.error(err?.message || "No pude subir la imagen pegada");
        }
      }
      return;
    }
    const text = e.clipboardData.getData("text/plain");
    if (text) {
      e.preventDefault();
      document.execCommand("insertText", false, text);
    }
  };

  const handleClick = (e) => {
    if (e.target.tagName === "IMG") {
      e.preventDefault();
      onImageClick?.(e.target.src);
    }
  };

  return (
    <div
      ref={ref}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onPaste={readOnly ? undefined : handlePaste}
      onInput={readOnly ? undefined : emit}
      onBlur={readOnly ? undefined : emit}
      onClick={handleClick}
      data-placeholder={readOnly ? "" : placeholder}
      data-testid={testId}
      className={`rich-notes mt-1 w-full border border-zinc-300 rounded px-3 py-2 text-sm ${readOnly ? "bg-zinc-50 text-zinc-700" : "focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] bg-white"}`}
    />
  );
}

/** Extract all <img src=...> URLs from a notes HTML blob (used for lightbox carousel). */
export function extractImagesFromNotes(html) {
  if (!html) return [];
  const out = [];
  const re = /<img\b[^>]*src=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html)) !== null) out.push(m[1]);
  return out;
}
